import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, CircularProgress, Collapse, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import useAuth from 'src/hooks/useAuth';
import { completeTemplateDraftAssignment } from 'src/utils/templateParameter';
import { assignmentContent, getAssignmentJob, getAssignmentJobs, newAssignmentKey } from 'src/api/assignmentJobs';

const active = (job) => ['queued', 'running', 'unknown'].includes(job.status);
const queryKey = (userId) => ['assignment-jobs', userId];

const refreshAssignmentResults = async (client, job) => {
  const parameter = job.kind !== 'application';
  const filter = {
    predicate: ({ queryKey: key }) => parameter
      ? /^(parameter-|parameters-|device-parameter|model-type-|tag-|template-parameter|(?:audit|api|sync|user)-log)/.test(key[0])
      : /^(device-|application-|(?:audit|api|sync|user)-log)/.test(key[0]),
  };
  // Drop reads started before completion, including inactive copies of values
  // that may now be masked. This preserves the synchronous form's protection.
  await client.cancelQueries(filter);
  if (parameter) client.removeQueries({ ...filter, type: 'inactive' });
  await client.invalidateQueries(filter);
};

const useAssignmentJobs = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: queryKey(user?.id),
    queryFn: ({ signal }) => getAssignmentJobs(signal),
    enabled: user?.id != null,
    refetchOnWindowFocus: true,
    retry: false,
    refetchInterval: (query) => query.state.status === 'error' || query.state.data?.results?.some(active) ? 3000 : false,
  });
};

// The attempt stays in memory only: parameter definitions/values are never put
// in browser storage. Accepted work is recovered from the owner-scoped API.
export const useAssignmentSubmission = () => {
  const client = useQueryClient();
  const { user } = useAuth();
  const owner = user?.id;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const session = useRef({ alive: false, generation: 0 });
  const attempt = useRef(null);
  const sending = useRef(false);
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [job, setJob] = useState(null);
  useEffect(() => {
    session.current = { alive: true, generation: session.current.generation + 1 };
    attempt.current = null;
    sending.current = false;
    setPending(false);
    setUncertain(false);
    setJob(null);
    return () => {
      session.current.alive = false;
      session.current.generation += 1;
      attempt.current = null;
      sending.current = false;
    };
  }, [owner]);
  const isCurrent = (current) => current && session.current.alive
    && current.generation === session.current.generation && current.owner === ownerRef.current;
  const finish = (current) => {
    if (isCurrent(current)) { sending.current = false; setPending(false); }
  };
  const remember = (next) => {
    setJob(next);
    if (!active(next)) refreshAssignmentResults(client, next);
    if (next.status === 'completed') completeTemplateDraftAssignment(next.job_id, owner);
    if (owner != null) {
      client.setQueryData(queryKey(owner), (previous) => ({
        results: [next, ...(previous?.results || []).filter((item) => item.job_id !== next.job_id)],
      }));
      client.invalidateQueries({ queryKey: queryKey(owner) });
    }
  };
  const run = async (current) => {
    if (!isCurrent(current) || sending.current) return;
    sending.current = true;
    setPending(true);
    setUncertain(false);
    let response;
    try {
      response = await current.execute(current.payload, current.key);
      if (response?.status === 202 && !assignmentContent(response)?.job_id) throw new Error('Assignment acceptance could not be confirmed');
    } catch (error) {
      if (isCurrent(current)) {
        const unknown = !error?.response || error.response.status >= 500;
        setUncertain(unknown);
        if (!unknown) attempt.current = null;
        current.onError?.(error, { uncertain: unknown });
      }
      finish(current);
      return;
    }
    if (!isCurrent(current)) {
      // Navigating away does not cancel server work. Refresh only its owner's
      // existing cache; never replay an old payload or run page callbacks.
      if (response?.status === 202 && current.owner != null) client.invalidateQueries({ queryKey: queryKey(current.owner) });
      return;
    }
    try {
      const accepted = response?.status === 202 ? assignmentContent(response) : null;
      if (accepted) remember(accepted);
      attempt.current = null;
      await current.onSuccess?.(response, { job: accepted });
    } finally { finish(current); }
  };
  return {
    pending, uncertain, job, ownerId: owner,
    start: (options) => {
      if (sending.current || attempt.current || job) return;
      const current = { ...options, owner, generation: session.current.generation,
        key: newAssignmentKey(), payload: JSON.parse(JSON.stringify(options.payload)) };
      current.onStart?.(current.payload, current.key);
      attempt.current = current;
      return run(current);
    },
    retry: async () => {
      const current = attempt.current;
      if (!isCurrent(current) || sending.current) return;
      sending.current = true;
      setPending(true);
      try {
        const recovered = await getAssignmentJob(current.key);
        if (!isCurrent(current)) return;
        if (recovered?.job_id) {
          remember(recovered);
          attempt.current = null;
          setUncertain(false);
          await current.onSuccess?.({ status: 202, data: { content: recovered } }, { job: recovered });
          return;
        }
      } catch (_) { /* The original key makes a repeated POST safe. */ }
      finally { finish(current); }
      if (isCurrent(current) && attempt.current === current) return run(current);
    },
    clear: () => { if (!sending.current && !uncertain) { attempt.current = null; setJob(null); } },
  };
};

export const AssignmentSubmissionNotice = ({ submission }) => submission.uncertain ? (
  <Alert severity="warning" action={<Button disabled={submission.pending} color="inherit" onClick={submission.retry}>Check / retry</Button>}>
    The response was lost. The assignment may already be running. Check its status or retry the same request; your selections are kept.
  </Alert>
) : submission.pending ? (
  <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={18} /><Typography variant="body2">Starting assignment…</Typography></Stack>
) : null;

const JobDisplay = ({ job }) => {
  const running = active(job);
  const failed = job.status === 'failed';
  const warning = job.status === 'completed_with_errors';
  const label = failed ? 'Assignment failed' : warning ? 'Assignments completed with errors'
    : job.status === 'completed' ? 'Assignments completed' : job.status === 'queued' ? 'Assignment queued' : 'Assignments in progress';
  const numeric = Number.isFinite(job.total) && job.total > 0;
  const kind = { application: 'Application assignment', parameter_create: 'Create parameter', parameter_update: 'Update parameter', parameter_tag: 'Tag parameter assignment' }[job.kind] || 'Parameter assignment';
  return <Stack spacing={1} role="status" aria-live="polite">
    <Typography variant="caption" color="text.secondary">{kind}{job.created_at ? ` · ${new Date(job.created_at).toLocaleString()}` : ''}</Typography>
    <Typography variant="subtitle2">{label}</Typography>
    {running && (numeric ? <>
      <LinearProgress aria-label="Assignment progress" variant="determinate" value={Math.min(100, 100 * job.processed / job.total)} />
      <Typography variant="body2">{job.processed} / {job.total} devices processed</Typography>
    </> : <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">{job.status === 'queued' ? 'Waiting for a worker…' : 'Updating server assignments…'}</Typography></Stack>)}
    <Typography variant="body2" color="text.secondary">{running ? 'You can leave this page. Work continues in the background.' : 'This result is for server assignments. Devices apply changes on their next successful sync.'}</Typography>
    {!running && <Alert severity={failed ? 'error' : warning ? 'warning' : 'success'}>
      {job.total == null
        ? (failed ? 'The parameter operation could not be completed.' : 'Parameter changes saved on the server.')
        : <>{job.succeeded ?? 0} succeeded · {job.skipped ?? 0} skipped · {job.failed ?? 0} failed
          {job.push_failed > 0 && ` · ${job.push_failed} notifications failed`}</>}
    </Alert>}
    {!running && job.result?.parameter_id && <Button size="small" sx={{ alignSelf: 'flex-start' }} href={`/dashboard/parameters/${job.result.parameter_id}`}>Open parameter</Button>}
    {job.message && <Typography variant="body2">{job.message}</Typography>}
    {job.errors?.length > 0 && <Box component="details"><Typography component="summary" variant="body2">Error details ({job.errors.length})</Typography>
      {job.errors.map((error, index) => <Typography key={index} variant="body2">{error.device_id ? `Device ${error.device_id}: ` : ''}{error.message || error.error || error.code || 'Assignment failed'}</Typography>)}
    </Box>}
  </Stack>;
};

export const AssignmentJobStatus = ({ job, onComplete }) => {
  const { user } = useAuth();
  const client = useQueryClient();
  const jobs = useAssignmentJobs();
  const listed = jobs.data?.results?.find((item) => item.job_id === job.job_id);
  // The dashboard history is bounded. Keep an explicitly opened job observable
  // by UUID even when newer work has pushed it out of that history.
  const detail = useQuery({
    queryKey: ['assignment-job', user?.id, job.job_id],
    queryFn: async () => {
      const result = await getAssignmentJob(job.job_id);
      if (result?.job_id !== job.job_id) throw new Error('Assignment status unavailable');
      return result;
    },
    enabled: user?.id != null && jobs.isSuccess && !listed,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => query.state.status === 'error' || (query.state.data && active(query.state.data)) ? 3000 : false,
  });
  const latest = listed || detail.data || job;
  const notified = useRef(null);
  useEffect(() => {
    if (!active(latest) && notified.current !== latest.job_id) {
      notified.current = latest.job_id;
      if (!listed) refreshAssignmentResults(client, latest);
      if (latest.status === 'completed') completeTemplateDraftAssignment(latest.job_id, user?.id);
      onComplete?.(latest);
    }
  }, [latest, listed, client, user?.id, onComplete]);
  return <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}><JobDisplay job={latest} />
    {(jobs.isError || detail.isError) && <Typography variant="caption" color="warning.main">Reconnecting to assignment status…</Typography>}
  </Box>;
};

export const AssignmentJobsIndicator = () => {
  const { user } = useAuth();
  const client = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const jobs = useAssignmentJobs();
  const [expanded, setExpanded] = useState(false);
  const observed = useRef(new Map());
  useEffect(() => {
    observed.current.clear();
    setExpanded(false);
    client.cancelQueries({ predicate: ({ queryKey: key }) => ['assignment-jobs', 'assignment-job'].includes(key[0]) && key[1] !== user?.id });
    client.removeQueries({ predicate: ({ queryKey: key }) => ['assignment-jobs', 'assignment-job'].includes(key[0]) && key[1] !== user?.id });
  }, [user?.id, client]);
  useEffect(() => {
    (jobs.data?.results || []).forEach((job) => {
      const previous = observed.current.get(job.job_id);
      if (job.status === 'completed') completeTemplateDraftAssignment(job.job_id, user?.id);
      if (previous && active(previous) && !active(job)) {
        enqueueSnackbar(job.status === 'completed' ? 'Server assignments completed. Devices apply changes on their next sync.' : 'Assignment finished with errors. Open Assignments for details.', { variant: job.status === 'completed' ? 'success' : 'warning' });
        refreshAssignmentResults(client, job);
      }
      observed.current.set(job.job_id, job);
    });
  }, [jobs.data, client, enqueueSnackbar, user?.id]);
  const rows = jobs.data?.results || [];
  if (user?.id == null) return null;
  if (!rows.length) return jobs.isError ? <Alert severity="warning" sx={{ m: 2 }}>Reconnecting to assignment status…</Alert> : null;
  const count = rows.filter(active).length;
  return <Paper variant="outlined" sx={{ m: 2, p: 1.5 }}>
    <Button size="small" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>Assignments · {count} active{count === 0 ? ' · recent results' : ''}</Button>
    {count > 0 && <CircularProgress size={14} sx={{ ml: 1 }} />}
    {jobs.isError && <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>Reconnecting to assignment status…</Typography>}
    <Collapse in={expanded}><Stack spacing={2} sx={{ maxHeight: 320, overflow: 'auto', mt: 1 }}>
      {rows.map((job) => <Box key={job.job_id} sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}><JobDisplay job={job} /></Box>)}
    </Stack></Collapse>
  </Paper>;
};
